import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { validerContrat } from '../services/tarificationService';

/**
 * Hook pour récupérer et gérer les paramètres tarifaires d'un contrat client
 */

export const useClientContract = (db, clientId) => {
  const [contrat, setContrat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validation, setValidation] = useState({ valide: false, erreurs: [] });

  /**
   * Récupère le contrat client depuis Firestore
   */
  const fetchContrat = useCallback(async () => {
    if (!db || !clientId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const contratDoc = await getDoc(doc(db, 'contracts', clientId));

      if (contratDoc.exists()) {
        const data = contratDoc.data();

        // Structure standardisée du contrat
        const contratStandardise = {
          // Informations de base
          id: contratDoc.id,
          clientId: clientId,
          dateCreation: data.dateCreation || new Date().toISOString(),

          // Paramètres tarifaires
          contractType: data.contractType || 'prestation',
          weeklyHours: Number(data.weeklyHours) || 0,
          baseRateType: data.baseRateType || 'standard',

          // NOUVEAU : Liste des prestations (Multi-Contrats)
          prestations: data.prestations || [], // { id, type, weeklyHours, baseRateType }

          // Majorations applicables
          majorations: data.majorations || {
            weekend: false,
            holidays: false,
            may1: false
          },

          // Conditions contractuelles
          termination: data.termination || {
            noticeDays: 30,
            conditions: 'lettre recommandée avec accusé de réception'
          },
          modifications: data.modifications || {
            noticeHours: 48,
            conditions: 'téléphone, SMS ou mail'
          },

          // Tâches et planning
          tasksPerPeriod: data.tasksPerPeriod || {
            morning: [],
            noon: [],
            afternoon: [],
            evening: []
          },

          // Données originales pour référence
          rawData: data
        };

        setContrat(contratStandardise);

        // Valider le contrat
        const validationResult = validerContrat(contratStandardise);
        setValidation(validationResult);

      } else {
        // Pas de contrat existant, créer une structure par défaut
        const contratParDefaut = {
          id: null,
          clientId: clientId,
          dateCreation: new Date().toISOString(),
          contractType: 'prestation',
          weeklyHours: 0,
          baseRateType: 'standard',
          prestations: [],
          majorations: {
            weekend: false,
            holidays: false,
            may1: false
          },
          termination: {
            noticeDays: 30,
            conditions: 'lettre recommandée avec accusé de réception'
          },
          modifications: {
            noticeHours: 48,
            conditions: 'téléphone, SMS ou mail'
          },
          tasksPerPeriod: {
            morning: [],
            noon: [],
            afternoon: [],
            evening: []
          },
          estNouveau: true
        };

        setContrat(contratParDefaut);
        setValidation({ valide: true, erreurs: [] });
      }

    } catch (err) {
      console.error('Erreur lors de la récupération du contrat:', err);
      setError(err.message);
      setValidation({ valide: false, erreurs: ['Erreur de chargement du contrat'] });
    } finally {
      setLoading(false);
    }
  }, [db, clientId]);

  /**
   * Recharge le contrat
   */
  const rechargerContrat = useCallback(() => {
    fetchContrat();
  }, [fetchContrat]);

  /**
   * Vérifie si le contrat est valide pour la génération de devis
   */
  const estValidePourDevis = useCallback(() => {
    if (!contrat) return false;

    // Vérifications minimales pour un devis
    const erreurs = [];

    if (!contrat.contractType) {
      erreurs.push('Type de contrat requis');
    }

    if (contrat.weeklyHours < 0) {
      erreurs.push('Volume horaire hebdomadaire invalide');
    }

    if (!contrat.baseRateType) {
      erreurs.push('Type de tarif de base requis');
    }

    return {
      valide: erreurs.length === 0,
      erreurs
    };
  }, [contrat]);

  /**
   * Obtient les paramètres tarifaires simplifiés pour les calculs
   */
  const getParametresTarifaires = useCallback(() => {
    if (!contrat) return null;

    return {
      contractType: contrat.contractType,
      weeklyHours: contrat.weeklyHours,
      baseRateType: contrat.baseRateType,
      majorations: contrat.majorations,
      prestations: contrat.prestations || []
    };
  }, [contrat]);

  /**
   * Obtient un résumé des conditions contractuelles
   */
  const getResumeContrat = useCallback(() => {
    if (!contrat) return null;

    // Vérification sécurisée des majorations
    const majorationsActives = contrat.majorations ?
      Object.entries(contrat.majorations)
        .filter(([_, active]) => active)
        .map(([type]) => type) : [];

    const nbPrestations = contrat.prestations?.length || 0;
    const infoTarif = nbPrestations > 0
      ? `Multi-Services (${nbPrestations})`
      : (contrat.baseRateType === '70plus' ? '70+' : 'Standard');

    return {
      type: contrat.contractType || 'prestation',
      volumeHebdo: contrat.weeklyHours || 0,
      tarifBase: infoTarif,
      majorations: majorationsActives,
      preavisResiliation: `${contrat.termination?.noticeDays || 30} jours`,
      preavisModification: `${contrat.modifications?.noticeHours || 48} heures`
    };
  }, [contrat]);

  /**
   * Charge le contrat au montage du composant
   */
  useEffect(() => {
    fetchContrat();
  }, [fetchContrat]);

  return {
    // État
    contrat,
    loading,
    error,
    validation,

    // Méthodes
    rechargerContrat,
    estValidePourDevis,
    getParametresTarifaires,
    getResumeContrat,

    // Propriétés dérivées
    aContrat: !!contrat && !contrat.estNouveau,
    estNouveau: contrat?.estNouveau || false,
    parametresTarifaires: getParametresTarifaires(),
    resumeContrat: getResumeContrat()
  };
};

/**
 * Hook pour gérer les contrats de plusieurs clients
 */
export const useClientContracts = (db, clientIds) => {
  const [contrats, setContrats] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  const chargerContrats = useCallback(async () => {
    if (!db || !clientIds || clientIds.length === 0) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErrors({});

      const contratsCharges = {};
      const erreursChargement = {};

      // Charger chaque contrat en parallèle
      await Promise.all(
        clientIds.map(async (clientId) => {
          try {
            const contratDoc = await getDoc(doc(db, 'contracts', clientId));

            if (contratDoc.exists()) {
              contratsCharges[clientId] = {
                id: contratDoc.id,
                clientId,
                ...contratDoc.data()
              };
            } else {
              contratsCharges[clientId] = null; // Pas de contrat
            }
          } catch (err) {
            console.error(`Erreur chargement contrat client ${clientId}:`, err);
            erreursChargement[clientId] = err.message;
            contratsCharges[clientId] = null;
          }
        })
      );

      setContrats(contratsCharges);
      setErrors(erreursChargement);

    } catch (err) {
      console.error('Erreur lors du chargement des contrats:', err);
      setErrors({ general: err.message });
    } finally {
      setLoading(false);
    }
  }, [db, clientIds]);

  useEffect(() => {
    chargerContrats();
  }, [chargerContrats]);

  return {
    contrats,
    loading,
    errors,
    recharger: chargerContrats
  };
};

export default useClientContract;
